import type { OrderedLine } from '../commerce/orderedLines';
import type { ReactNode } from 'react';
import { ThemePageTypeEnum, type CodCheckoutDisplay } from '@xshop/shared';
import type { StorefrontBundle, StorefrontBundleTier, StorefrontProduct } from '../api/client';
import type { PageType } from '../themes/registry';
import type { ProductFacts } from '../elements/render/storeFacts';
import { productStock } from '../commerce/variantStock';

// The contract between the storefront shell and the page it draws: the shell resolves the shop once,
// and hands that one shape to the element tree. Nothing below this file fetches, prices or tracks.

/**
 * A theme's handle. Not an allowlist: a theme is a palette and a tree of the same elements every other
 * theme is drawn from, so this bundle can draw any of them and has nothing to gain from knowing their
 * names. It used to be `['base'] as const`, which made a theme authored after the last build invisible —
 * `isThemeSlug` answered false, the editor resolved no theme, and the builder could not open it at all.
 */
export type ThemeSlug = string;

/** The slug grammar `ThemeDefinitionSchema` writes a theme with, so both ends agree on what a handle is. */
const THEME_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The page vocabulary as a value, so a query parameter can be checked against it. */
export const PAGE_TYPES: readonly PageType[] = ThemePageTypeEnum.options;

// Reports whether a value is shaped like a theme handle, which is all a query parameter can be judged on.
export const isThemeSlug = (value: string | null | undefined): value is ThemeSlug =>
  typeof value === 'string' && value.length <= 50 && THEME_SLUG_RE.test(value);

// Reports whether a query value names a page a theme could ship.
export const isPageType = (value: string | null | undefined): value is PageType =>
  typeof value === 'string' && (PAGE_TYPES as readonly string[]).includes(value);

/** A created order exactly as the API answered it; the receipt renders every column this does not name. */
export type PlacedOrder = { id?: string; [field: string]: unknown };

/** A placed order, the same columns the receipt already reads off a completed COD checkout. */
export type ShellOrder = {
  /** The lines the order held, kept from the cart as it was placed; the order the server returns has none. */
  lines?: OrderedLine[];
  orderNumber: string;
  customerFullName: string;
  customerPhone: string;
  shippingCity: string;
  shippingAddress: string;
  totalPrice: string | number;
  /** The order's money rows, for the thank-you page's summary: after the offers, what was taken off, the delivery. */
  subtotalPrice?: number;
  discountAmount?: number;
  shippingPrice?: number;
  /** The discount's name as the checkout showed it; the order the server returns carries none. */
  discountLabel?: string | null;
  paymentMethod: string;
  currency?: string;
};

/** Everything the shell already resolved about this shop, handed to whatever the page draws. */
export type ShellData = {
  storeName: string;
  currency: string;
  /** How money is spelled on this visit — the theme's own reading direction decides it, once. */
  moneyLocale: 'ar-MA' | 'fr-MA';
  product: StorefrontProduct;
  /** The catalog page the shell already loaded. */
  products: readonly StorefrontProduct[];
  images: StorefrontProduct['images'];
  unitPrice: number;
  compareAtPrice: number | null;
  quantity: number;
  /** Units in the one line a checkout opened now would order — the whole of this storefront's cart. */
  cartCount: number;
  /** Priced by the server against the variant that will be ordered; nothing multiplies it again. */
  merchandiseTotal: number;
  bundle: StorefrontBundle | null;
  selectedTier: StorefrontBundleTier | null;
  onSelectTier: (tier: StorefrontBundleTier) => void;
  /** The single `OnePageCheckout` element the shell built, which is where checkout tracking lives. */
  checkoutForm: ReactNode;
  display: CodCheckoutDisplay;
  isOpen: boolean;
  /** Reaching the checkout the way the shell decided: a scroll when embedded, an open when not. */
  openCheckout: () => void;
  onOpen: () => void;
  onClose: () => void;
  order: ShellOrder | null;
};

/** How long a product is marked new after it is added: thirty days. */
const NEW_FOR_MS = 30 * 86_400_000;

/** What the shell already priced about the line it is selling, which is all a bound prop may name. */
export type ProductPricing = { unitPrice: number; compareAtPrice: number | null; stock?: number | null };

/**
 * Narrows the product to the handful of facts a bound prop may read. A shop with no orderable line has
 * no product to bind to, and says so with null rather than with a row of empty strings — which is what
 * lets a bound heading keep the merchant's own words on a page that sells nothing.
 */
export function productFactsOf(
  product: StorefrontProduct,
  pricing: ProductPricing,
  images: StorefrontProduct['images']
): ProductFacts | null {
  if (product.id === '') return null;
  const discountPercent = pricing.compareAtPrice && pricing.compareAtPrice > pricing.unitPrice
    ? Math.round((1 - pricing.unitPrice / pricing.compareAtPrice) * 100) : 0;
  const isNew = Boolean(product.createdAt && Date.now() - Date.parse(product.createdAt) < NEW_FOR_MS);
  return {
    title: product.title,
    type: product.productType ?? '',
    hoverImage: images[1]?.src ?? '',
    badge: discountPercent ? `${discountPercent}% OFF` : isNew ? 'New' : '',
    discountPercent,
    isNew,
    href: product.id.startsWith('sample-') ? `/?page=product&sample=${product.id.slice(7)}` : `/products/${encodeURIComponent(product.slug)}`,
    price: pricing.unitPrice,
    compareAtPrice: pricing.compareAtPrice,
    description: product.descriptionHtml ?? '',
    image: images[0]?.src ?? '',
    stock: pricing.stock !== undefined ? pricing.stock : productStock(product),
    rating: product.ratingAverage ?? 0,
    ratingCount: product.ratingCount ?? 0
  };
}

/** The pages that sell the shell's own line; on the rest the shopper has chosen nothing to order. */
const SELLING_PAGES: readonly PageType[] = ['product', 'product-cod', 'checkout'];

/**
 * Counts what the cart holds. This storefront keeps no basket: a checkout posts exactly one line —
 * this product's variant at the selected bundle tier's quantity — so the units in that pending line
 * are the whole cart. A page that sells nothing, and a shop with nothing orderable, hold none.
 */
export const cartUnits = (product: StorefrontProduct, quantity: number, page: PageType): number => {
  if (!SELLING_PAGES.includes(page) || !product.id || product.variants.length === 0) return 0;
  return Math.max(0, Math.trunc(quantity));
};

// Reads a money column the API sends as a number or a decimal string, or nothing when it is absent.
const amountOf = (value: unknown): number | undefined => {
  const amount = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(amount) ? amount : undefined;
};

// Reads the columns a thank-you page recaps off a completed checkout, or null when none was placed.
export const readShellOrder = (raw: unknown): ShellOrder | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.orderNumber !== 'string') return null;
  const text = (key: string): string => (typeof row[key] === 'string' ? (row[key] as string) : '');
  return {
    orderNumber: row.orderNumber,
    customerFullName: text('customerFullName'),
    customerPhone: text('customerPhone'),
    shippingCity: text('shippingCity'),
    shippingAddress: text('shippingAddress'),
    totalPrice: typeof row.totalPrice === 'number' ? row.totalPrice : text('totalPrice'),
    paymentMethod: text('paymentMethod') || 'COD',
    currency: typeof row.currency === 'string' ? row.currency : undefined,
    subtotalPrice: amountOf(row.subtotalPrice),
    discountAmount: amountOf(row.discountAmount),
    shippingPrice: amountOf(row.shippingPrice),
    discountLabel: typeof row.discountLabel === 'string' ? row.discountLabel : null
  };
};
